import type { ComponentProps } from "react"

import { cn } from "@coval/ui/cn"
import { Streamdown } from "streamdown"

import { SOFIA_MARKDOWN_COMPONENTS } from "../SofiaMarkdownLink"

interface NotebookTableProps {
  columns: string[]
  rows: Record<string, unknown>[]
  remarkPlugins: ComponentProps<typeof Streamdown>["remarkPlugins"]
}

/**
 * A cell only pays for the markdown renderer when it can carry a citation:
 * a markdown link, one of Sofia's `coval://` entity tokens, or a plain URL.
 */
const CITATION_RE = /\]\(|coval:\/\/|https?:\/\//

/** Inline markdown inside a table cell: no paragraph spacing, links styled like prose. */
const CELL_MARKDOWN_CLASSES =
  "[&_p]:m-0 [&_p]:inline [&_a]:text-coval-info-text [&_a]:underline"

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return ""
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(2)
  }
  if (typeof value === "string" || typeof value === "boolean") {
    return String(value)
  }
  return JSON.stringify(value)
}

/**
 * Sofia cites a conversation inside a table cell the same way it does in prose,
 * so a cell that carries a citation renders through the same markdown renderer
 * and entity-link plugins. Everything else stays plain text.
 */
function CellContent({
  value,
  remarkPlugins,
}: {
  value: unknown
  remarkPlugins: NotebookTableProps["remarkPlugins"]
}) {
  const text = formatCell(value)
  if (!CITATION_RE.test(text)) {
    return text
  }
  return (
    <span className={CELL_MARKDOWN_CLASSES}>
      <Streamdown
        components={SOFIA_MARKDOWN_COMPONENTS}
        remarkPlugins={remarkPlugins}
      >
        {text}
      </Streamdown>
    </span>
  )
}

/** Bounded result preview. Rows come from Sofia's chart payload or its answer, never a raw result set. */
export function NotebookTable({
  columns,
  rows,
  remarkPlugins,
}: NotebookTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-coval-border">
      <table className="w-full text-sm">
        <thead className="bg-surface-1 text-left text-xs uppercase tracking-wide text-coval-text-muted">
          <tr>
            {columns.map((column) => (
              <th key={column} className="px-3 py-2 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={cn(
                "border-t border-coval-border",
                rowIndex % 2 === 1 && "bg-surface-1/40",
              )}
            >
              {columns.map((column) => {
                const value = row[column]
                return (
                  <td
                    key={column}
                    className={cn(
                      "px-3 py-1.5 text-coval-text",
                      typeof value === "number" && "tabular-nums text-right",
                    )}
                  >
                    <CellContent value={value} remarkPlugins={remarkPlugins} />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
